import { HubConnectionState } from "@microsoft/signalr";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from "@progress/kendo-react-inputs";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type SubmitEvent } from "react";
import { useLazyGetChatConversationQuery } from "../api/chatApi";
import { useAppDispatch } from "../app/hooks";
import { ChatMessageType, type ChatMessageDto } from "../contracts";
import type { ChatConversationViewModel } from "../models/chatMessageViewModel";
import { clearCurrentCommercialPlan } from "../slices/chatSlice";
import type { ChatConversationRuntime } from "./context/chatConversationContext";
import { ChatConversationProvider } from "./context/chatConversationProvider";
import ToolRenderer from "./tools/toolRenderer";
import { useChatController, type ConversationChangedOptions } from "./controller/useChatController";
import { isCurrentMessageToolRequest } from "../models/toolRequestModels";

export type { ConversationChangedOptions };

export type ChatConversationSelectionHandler = (conversationId: number | undefined) => Promise<void>;

export type SimulatorStartedAnimationOptions = {
    conversationId?: number;
    title: string;
    summary: string;
    sourceRect?: DOMRect;
};

export type ChatConversationRef = {
    selectConversationAsync: ChatConversationSelectionHandler;
    startNewConversation: () => void;
};

export type ChatConversationProps = {
    conversationId?: number;
    initialConversation?: ChatConversationViewModel;
    badgeText?: string;
    placeholder?: string;
    submitLabel?: string;
    onConversationChanged?: (conversationId: number | undefined, options?: ConversationChangedOptions) => void;
};

const shouldRenderChatMessage = ({ type }: ChatMessageDto): boolean =>
    [
        ChatMessageType.User,
        ChatMessageType.Assistant,
        ChatMessageType.ToolRequest
    ].includes(type);

const ChatConversation = forwardRef<ChatConversationRef, ChatConversationProps>((
    {
        conversationId,
        initialConversation,
        badgeText = "ASK SOVRA AI",
        placeholder = "e.g. For SKU Finish Quantum 60pk at Chemist Warehouse propose the optimal 12 week mix of price, promotion and retail media to maximise revenue and profit.",
        submitLabel = "GO",
        onConversationChanged
    },
    ref
) => {
    const dispatch = useAppDispatch();
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const startSimulationSourceRef = useRef<HTMLFormElement | null>(null);
    const loadedConversationIdRef = useRef<number | undefined>(initialConversation?.id);
    const failedConversationIdRef = useRef<number | undefined>(undefined);

    const [loadConversationAsync, { isFetching: isFetchingConversation }] = useLazyGetChatConversationQuery();
    const [loadConversationError, setLoadConversationError] = useState<string | undefined>();

    const handleControllerConversationChanged = useCallback((changedConversationId: number | undefined, options?: ConversationChangedOptions): void => {
        loadedConversationIdRef.current = changedConversationId;
        onConversationChanged?.(changedConversationId, options);
    }, [onConversationChanged]);

    const {
        chatMessages,
        query,
        setQuery,
        hasError,
        isChatBusy,
        errorMessage,
        clearError,
        signalRStatus,
        connectionState,
        submitQueryAsync,
        actions,
        replaceConversation,
        startNewConversation
    } = useChatController({
        onConversationChanged: handleControllerConversationChanged
    });

    const runtime = useMemo<ChatConversationRuntime>(() => ({
        chatMessages,
        isChatBusy,
        actions
    }), [
        chatMessages,
        isChatBusy,
        actions
    ]);

    const scrollChatToBottom = useCallback((): void => {
        if (!chatContainerRef.current) {
            return;
        }
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }, []);

    const handleStartNewConversation = useCallback((): void => {
        loadedConversationIdRef.current = undefined;
        failedConversationIdRef.current = undefined;
        startNewConversation();
        setLoadConversationError(undefined);
        dispatch(clearCurrentCommercialPlan());
    }, [dispatch, startNewConversation]);

    const handleSelectConversationAsync = useCallback(async (conversationId: number | undefined): Promise<void> => {
        if (conversationId == null) {
            if (loadedConversationIdRef.current != null) {
                handleStartNewConversation();
            }

            return;
        }

        if (
            conversationId <= 0
            || conversationId === loadedConversationIdRef.current
            || conversationId === failedConversationIdRef.current
            || isFetchingConversation
        ) {
            return;
        }

        failedConversationIdRef.current = undefined;
        setLoadConversationError(undefined);
        clearError();

        try {
            const response = await loadConversationAsync({
                chat_conversation_id: conversationId
            }).unwrap();

            loadedConversationIdRef.current = response.chat_conversation.id;
            failedConversationIdRef.current = undefined;
            replaceConversation(response.chat_conversation);
        } catch (error) {
            failedConversationIdRef.current = conversationId;
            console.error("Error loading simulator chat conversation.", error);
            setLoadConversationError(error instanceof Error ? error.message : "Unable to load conversation. Please try again.");
        }
    }, [clearError, handleStartNewConversation, isFetchingConversation, loadConversationAsync, replaceConversation]);

    useImperativeHandle(ref, () => ({
        selectConversationAsync: handleSelectConversationAsync,
        startNewConversation: handleStartNewConversation
    }), [handleSelectConversationAsync, handleStartNewConversation]);

    useEffect(() => {
        if (conversationId == null) {
            if (loadedConversationIdRef.current == null) {
                return;
            }

            const timeoutId = window.setTimeout(() => {
                handleStartNewConversation();
            }, 0);

            return () => {
                window.clearTimeout(timeoutId);
            };
        }

        if (
            conversationId === loadedConversationIdRef.current
            || conversationId === failedConversationIdRef.current
        ) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            void handleSelectConversationAsync(conversationId);
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [conversationId, handleSelectConversationAsync, handleStartNewConversation]);

    useEffect(() => {
        scrollChatToBottom();
    }, [chatMessages, isChatBusy, hasError, scrollChatToBottom]);

    useEffect(() => {
        if (!hasError) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            clearError();
        }, 10000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [clearError, hasError, errorMessage]);

    useEffect(() => {
        if (!chatContainerRef.current) {
            return;
        }

        const mutationObserver = new MutationObserver(() => {
            scrollChatToBottom();
        });

        mutationObserver.observe(chatContainerRef.current, {
            childList: true,
            characterData: true,
            subtree: true
        });

        return () => {
            mutationObserver.disconnect();
        };
    }, [scrollChatToBottom]);

    const isToolCallInProgress = isCurrentMessageToolRequest(chatMessages);
    const isInputDisabled = isChatBusy || isFetchingConversation || isToolCallInProgress || connectionState !== HubConnectionState.Connected;
    const inputStyle = isInputDisabled
        ? {
                opacity: 0.65,
                cursor: "not-allowed",
                backgroundColor: "var(--bs-secondary-bg)",
                borderColor: "var(--bs-border-color)",
                boxShadow: "none"
            }
        : undefined;

    const handleSubmitQuery = async (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        await submitQueryAsync();
    };

    return (
        <ChatConversationProvider value={runtime}>
            <div className="bg-white shadow-sm h-100 d-flex flex-column">
                <div className="d-flex align-items-center m-2 pb-2 border-bottom">
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <span className="badge rounded-pill text-bg-success px-3 py-2">
                            {badgeText}
                        </span>
                    </div>

                    <div className="d-flex justify-content-center flex-grow-1">
                        <span
                            className={`badge rounded-pill text-bg-${signalRStatus.tone}-subtle text-${signalRStatus.tone}`}
                        >
                            {signalRStatus.text}
                        </span>
                    </div>

                    <div className="d-flex justify-content-end flex-grow-1">
                        <Button
                            type="button"
                            size="small"
                            fillMode="outline"
                            themeColor="success"
                            disabled={isFetchingConversation}
                            onClick={handleStartNewConversation}
                        >
                            New conversation
                        </Button>
                    </div>
                </div>
                {(hasError || loadConversationError) && (
                    <div className="alert alert-danger py-2 px-3 m-1 d-flex justify-content-between align-items-start gap-2" role="alert">
                        <span>{errorMessage ?? loadConversationError ?? "Your request timed out or an error occurred. Please try again."}</span>
                        <Button
                            type="button"
                            fillMode="flat"
                            size="small"
                            className="p-0 lh-1"
                            onClick={() => {
                                clearError();
                                setLoadConversationError(undefined);
                            }}
                            aria-label="Dismiss error"
                        >
                            ×
                        </Button>
                    </div>
                )}
                <div
                    className="d-flex flex-column gap-3 p-2 rounded-3 flex-grow-1 overflow-auto"
                    aria-live="polite"
                    aria-busy={isChatBusy || isFetchingConversation}
                    ref={chatContainerRef}
                >
                    {isFetchingConversation && (
                        <div className="d-flex justify-content-start">
                            <div
                                className="rounded-3 px-3 py-2 bg-light border d-inline-flex align-items-center gap-2 text-muted"
                                style={{ maxWidth: "80%" }}
                                role="status"
                            >
                                <Loader type="converging-spinner" size="small" />
                                <div>
                                    <p className="mb-1 fw-semibold text-dark">Loading conversation</p>
                                    <p className="mb-0 small">Fetching the selected conversation.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {chatMessages.filter(shouldRenderChatMessage).map(message => (
                        <div
                            key={`msg_${message.id ?? message.client_message_id}`}
                            className={`d-flex ${message.type === ChatMessageType.User ? "justify-content-end" : "justify-content-start"}`}
                        >
                            <div
                                className={`rounded-3 px-3 py-2 ${
                                    message.type === ChatMessageType.User
                                        ? "bg-success text-white"
                                        : message.type === ChatMessageType.ToolRequest
                                            ? "bg-light text-dark border shadow"
                                            : "bg-light border"
                                }`}
                                style={{ maxWidth: "80%" }}
                            >
                                <ToolRenderer message={message} />
                            </div>
                        </div>
                    ))}
                    {isChatBusy && (
                        <div className="d-flex justify-content-start">
                            <div
                                className="rounded-3 px-3 py-2 bg-light border d-inline-flex align-items-center gap-2 text-muted"
                                style={{ maxWidth: "80%" }}
                                role="status"
                            >
                                <Loader type="converging-spinner" size="small" />
                                <div>
                                    <p className="mb-1 fw-semibold text-dark">Sovra AI is processing your request</p>
                                    <p className="mb-0 small">Analysing your prompt and preparing a response.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <form ref={startSimulationSourceRef} onSubmit={handleSubmitQuery} className="m-2 pt-3 border-top flex-shrink-0">
                    <div className="d-flex flex-column flex-lg-row gap-2 align-items-stretch align-items-lg-center ">
                        <div className="flex-grow-1">
                            <Input
                                value={query}
                                placeholder={placeholder}
                                disabled={isInputDisabled}
                                style={inputStyle}
                                onChange={event => setQuery((event.value as string) ?? "")}
                            />
                        </div>
                        <Button
                            type="submit"
                            themeColor="success"
                            fillMode="solid"
                            size="small"
                            className="fw-semibold flex-shrink-0 px-3"
                            disabled={!query.trim() || isInputDisabled}
                        >
                            {isChatBusy || isFetchingConversation ? "Processing..." : submitLabel}
                        </Button>
                    </div>
                </form>
            </div>
        </ChatConversationProvider>
    );
});

export default ChatConversation;
